import React, { useEffect, useState, FormEvent } from 'react'
import useAppConfigStore, { AppConfig as AppConfigItem } from '../../store/AppConfigStore'
import './AppConfig.scss'

const AppConfig = () => {
  const { configs, loading, fetchConfigs, createConfig, updateConfig, deleteConfig } =
    useAppConfigStore()

  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editDesc, setEditDesc] = useState('')

  useEffect(() => {
    fetchConfigs()
  }, [])

  const parseValue = (raw: string): unknown => {
    if (raw === 'true') return true
    if (raw === 'false') return false
    if (!isNaN(Number(raw)) && raw.trim() !== '') return Number(raw)
    return raw
  }

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!newKey.trim() || newValue === '') return

    await createConfig(newKey.trim(), parseValue(newValue), newDesc.trim())
    setNewKey('')
    setNewValue('')
    setNewDesc('')
  }

  const handleEdit = (item: AppConfigItem) => {
    setEditingKey(item.key)
    setEditValue(typeof item.value === 'boolean' ? String(item.value) : String(item.value))
    setEditDesc(item.description || '')
  }

  const handleUpdate = async (key: string) => {
    await updateConfig(key, parseValue(editValue), editDesc.trim())
    setEditingKey(null)
  }

  const handleDelete = async (key: string) => {
    if (window.confirm(`Delete config "${key}"?`)) {
      await deleteConfig(key)
    }
  }

  const renderValueBadge = (value: unknown) => {
    if (typeof value === 'boolean') {
      return (
        <span className={`val-badge ${value ? 'val-true' : 'val-false'}`}>{String(value)}</span>
      )
    }
    return <span className="val-badge val-other">{String(value)}</span>
  }

  return (
    <div className="ac-page">
      <div className="ac-header">
        <div>
          <h3 className="ac-title">App Config</h3>
          <span className="ac-count">{configs.length} constants</span>
        </div>
      </div>

      {/* Create form */}
      <form className="ac-create-form" onSubmit={handleCreate}>
        <input
          className="ac-input"
          placeholder="Key (e.g. USE_TEST_ADS)"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          required
        />
        <input
          className="ac-input"
          placeholder="Value (true / false / number / string)"
          value={newValue}
          onChange={e => setNewValue(e.target.value)}
          required
        />
        <input
          className="ac-input ac-input-desc"
          placeholder="Description (optional)"
          value={newDesc}
          onChange={e => setNewDesc(e.target.value)}
        />
        <button className="ac-btn ac-btn-create" type="submit">
          Add
        </button>
      </form>

      {/* Table */}
      <div className="ac-card">
        {loading && !configs.length ? (
          <div className="ac-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="ac-skel-row">
                <div className="ac-skel-line w50" />
                <div className="ac-skel-line w30" />
              </div>
            ))}
          </div>
        ) : configs.length ? (
          <div className="ac-table-wrap">
            <table className="ac-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Key</th>
                  <th>Value</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((item, index) => (
                  <tr key={item.key || index}>
                    <td className="col-num">{index + 1}</td>
                    <td className="col-key">
                      <code>{item.key}</code>
                    </td>
                    <td className="col-value">
                      {editingKey === item.key ? (
                        <input
                          className="ac-input ac-input-sm"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        renderValueBadge(item.value)
                      )}
                    </td>
                    <td className="col-desc">
                      {editingKey === item.key ? (
                        <input
                          className="ac-input ac-input-sm"
                          value={editDesc}
                          onChange={e => setEditDesc(e.target.value)}
                        />
                      ) : (
                        <span className="desc-text">{item.description || '—'}</span>
                      )}
                    </td>
                    <td className="col-actions">
                      {editingKey === item.key ? (
                        <>
                          <button
                            className="ac-btn ac-btn-save"
                            onClick={() => handleUpdate(item.key)}
                          >
                            Save
                          </button>
                          <button
                            className="ac-btn ac-btn-cancel"
                            onClick={() => setEditingKey(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="ac-btn ac-btn-edit" onClick={() => handleEdit(item)}>
                            Edit
                          </button>
                          <button
                            className="ac-btn ac-btn-delete"
                            onClick={() => handleDelete(item.key)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="ac-empty">
            <p>No config entries yet. Add one above.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default AppConfig
